// C:\Users\ADMIN\Desktop\ERP\myerp\my-frontend\src\pages\etc\ExpenseStatus.jsx
import React, { useEffect, useState } from "react";
import api from "../../api";
import styles from "./ExpenseStatus.module.css";

// ─── 상수 ───────────────────────────────────────────────────────────────────
const UNITS = [
  {
    key: "경영팀",
    url: "/expense/business/",
    label: "경영팀(본사)",
    categoryField: "service_type",   // ExpenseBusinessNew 전송 필드
    group: "경영팀",
  },
  {
    key: "용역",
    url: "/expense/service/",
    label: "용역사업",
    categoryField: "category_name",
    group: "용역",
  },
  {
    key: "환경/소독",
    url: "/expense/env/disinfection/",
    label: "환경/소독",
    categoryField: "category_name",
    group: "환경",
  },
  {
    key: "환경/종합",
    url: "/expense/env/total/",
    label: "환경/종합",
    categoryField: "category_name",
    group: "환경",
  },
  {
    key: "카페_가산",
    url: "/expense/cafe/gasan/",
    label: "카페인 가산점",
    categoryField: "category_name",
    group: "카페",
  },
  {
    key: "카페_독산",
    url: "/expense/cafe/doksan/",
    label: "카페인 독산점",
    categoryField: "category_name",
    group: "카페",
  },
  {
    key: "카페_금나래",
    url: "/expense/cafe/geumnarae/",
    label: "카페인 금나래점",
    categoryField: "category_name",
    group: "카페",
  },

  
  {
    key: "카페_그린",
    url: "/expense/cafe/green/",
    label: "카페인 그린점",
    categoryField: "category_name",
    group: "카페",
  },
];

// ─── 유틸 ────────────────────────────────────────────────────────────────────
const parseAmount = (v) => {
  const n = Number(String(v ?? "0").replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const fmt = (n) => Number(n || 0).toLocaleString();

// ─── 컴포넌트 ────────────────────────────────────────────────────────────────
export default function ExpenseStatus() {
  const year = new Date().getFullYear();

  const [fromDate, setFromDate] = useState(`${year}-01-01`);
  const [toDate, setToDate] = useState(`${year}-12-31`);
  const [loading, setLoading] = useState(false);

  // data 구조: { [unitKey]: { supply, vat, byCategory: { [catName]: { supply, vat, byPartner: { [name]: { supply, vat } } } } } }
  const [data, setData] = useState({});
  const [expanded, setExpanded] = useState({});

  // ── 데이터 로딩 ────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      setLoading(true);
      const params = { start_date: fromDate, end_date: toDate };
      const result = {};

      await Promise.all(
        UNITS.map(async (unit) => {
          let page = 1;
          let hasNext = true;
          let supply = 0;
          let vat = 0;
          const byCategory = {};

          while (hasNext && page <= 200) {
            try {
              const res = await api.get(unit.url, { params: { ...params, page } });
              const list = Array.isArray(res.data)
                ? res.data
                : res.data?.results ?? [];

              list.forEach((item) => {
                const s = parseAmount(item.supply_amount);
                const v = parseAmount(item.vat);
                supply += s;
                vat += v;

                // 구분명: 경영팀은 service_type, 나머지는 category_name
                const catName =
                  (item[unit.categoryField] ||
                    item.category_name ||
                    item.service_type ||
                    "기타"
                  ).trim();

                if (!byCategory[catName]) {
                  byCategory[catName] = { supply: 0, vat: 0, byPartner: {} };
                }
                byCategory[catName].supply += s;
                byCategory[catName].vat += v;

                // 거래처 집계: partners 배열 우선, 없으면 partner_name 단건
                const partnerArr =
                  Array.isArray(item.partners) && item.partners.length > 0
                    ? item.partners
                    : [{ name: item.partner_name || "미지정" }];

                // 여러 거래처가 있을 때는 금액을 n등분
                const perPart = partnerArr.length > 0 ? s / partnerArr.length : s;
                const perPartVat = partnerArr.length > 0 ? v / partnerArr.length : v;

                partnerArr.forEach((p) => {
                  const pName = (p.name || "미지정").trim();
                  if (!byCategory[catName].byPartner[pName]) {
                    byCategory[catName].byPartner[pName] = { supply: 0, vat: 0 };
                  }
                  byCategory[catName].byPartner[pName].supply += perPart;
                  byCategory[catName].byPartner[pName].vat += perPartVat;
                });
              });

              hasNext = res.data && typeof res.data === "object" && !!res.data.next;
              page += 1;
            } catch (err) {
              console.warn(`[${unit.label}] 로딩 실패`, err);
              break;
            }
          }

          result[unit.key] = { supply, vat, byCategory };
        })
      );

      setData(result);

      // 기본적으로 그룹 헤더만 열어두기
      setExpanded({
        그룹_경영팀: true,
        그룹_용역: true,
        그룹_환경: true,
        그룹_카페: true,
      });
      setLoading(false);
    })();
  }, [fromDate, toDate]);

  // ── 토글 ──────────────────────────────────────────────────────────────────
  const toggle = (key) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  // ── 합계 계산 헬퍼 ────────────────────────────────────────────────────────
  const unitTotal = (key) => {
    const d = data[key];
    return d ? { supply: d.supply, vat: d.vat, total: d.supply + d.vat } : { supply: 0, vat: 0, total: 0 };
  };

  const groupTotal = (keys) =>
    keys.reduce(
      (acc, k) => {
        const t = unitTotal(k);
        return { supply: acc.supply + t.supply, vat: acc.vat + t.vat, total: acc.total + t.total };
      },
      { supply: 0, vat: 0, total: 0 }
    );

  const grandTotal = groupTotal(UNITS.map((u) => u.key));

  // ── 단위 행 렌더링 (사업부문 한 줄 + 구분명 + 거래처) ─────────────────────
  const renderUnit = (unit, level = 2) => {
    const d = data[unit.key];
    if (!d) return null;

    const t = unitTotal(unit.key);
    const unitId = `unit_${unit.key}`;
    const isOpen = expanded[unitId];
    const catEntries = Object.entries(d.byCategory);
    const hasCat = catEntries.length > 0;

    return (
      <React.Fragment key={unit.key}>
        {/* 사업부문 행 */}
        <div
          className={`${styles.row} ${styles[`level${level}`]}`}
          onClick={() => hasCat && toggle(unitId)}
          style={{ cursor: hasCat ? "pointer" : "default" }}
        >
          <div className={`${styles.cell} ${styles[`indent${level}`]}`}>
            {hasCat && (
              <span className={styles.arrow}>{isOpen ? "▼" : "▶"}</span>
            )}
            {unit.label}
          </div>
          <div className={styles.cellRight}>{fmt(t.supply)} 원</div>
          <div className={styles.cellRight}>{fmt(t.vat)} 원</div>
          <div className={styles.cellRight}>{fmt(t.total)} 원</div>
          <div className={styles.cellNote} />
        </div>

        {/* 구분명별 소계 */}
        {isOpen &&
          catEntries.map(([catName, catData]) => {
            const catId = `cat_${unit.key}_${catName}`;
            const isCatOpen = expanded[catId];
            const partnerEntries = Object.entries(catData.byPartner);
            const hasPart = partnerEntries.length > 0;
            const catTotal = catData.supply + catData.vat;

            return (
              <React.Fragment key={catId}>
                <div
                  className={`${styles.row} ${styles[`level${level + 1}`]}`}
                  onClick={() => hasPart && toggle(catId)}
                  style={{ cursor: hasPart ? "pointer" : "default" }}
                >
                  <div className={`${styles.cell} ${styles[`indent${level + 1}`]}`}>
                    {hasPart && (
                      <span className={styles.arrow}>
                        {isCatOpen ? "▼" : "▶"}
                      </span>
                    )}
                    🔹 {catName}
                    {hasPart && (
                      <span className={styles.partnerCount}>
                        거래처 {partnerEntries.length}개소
                      </span>
                    )}
                  </div>
                  <div className={styles.cellRight}>{fmt(catData.supply)} 원</div>
                  <div className={styles.cellRight}>{fmt(catData.vat)} 원</div>
                  <div className={styles.cellRight}>{fmt(catTotal)} 원</div>
                  <div className={styles.cellNote} />
                </div>

                {/* 거래처별 */}
                {isCatOpen &&
                  partnerEntries.map(([pName, pData]) => (
                    <div
                      key={pName}
                      className={`${styles.row} ${styles[`level${level + 2}`]}`}
                    >
                      <div className={`${styles.cell} ${styles[`indent${level + 2}`]}`}>
                        🏢 {pName}
                      </div>
                      <div className={styles.cellRight}>{fmt(pData.supply)} 원</div>
                      <div className={styles.cellRight}>{fmt(pData.vat)} 원</div>
                      <div className={styles.cellRight}>
                        {fmt(pData.supply + pData.vat)} 원
                      </div>
                      <div className={styles.cellNote} />
                    </div>
                  ))}
              </React.Fragment>
            );
          })}
      </React.Fragment>
    );
  };

  // ── 그룹 헤더 렌더링 ──────────────────────────────────────────────────────
  const renderGroup = (groupLabel, groupKey, unitKeys) => {
    const gt = groupTotal(unitKeys);
    const isOpen = expanded[`그룹_${groupKey}`];

    return (
      <React.Fragment key={groupKey}>
        <div
          className={`${styles.row} ${styles.level1}`}
          onClick={() => toggle(`그룹_${groupKey}`)}
        >
          <div className={`${styles.cell} ${styles.indent1}`}>
            <span className={styles.arrow}>{isOpen ? "▼" : "▶"}</span>
            {groupLabel}
          </div>
          <div className={styles.cellRight}>{fmt(gt.supply)} 원</div>
          <div className={styles.cellRight}>{fmt(gt.vat)} 원</div>
          <div className={styles.cellRight}>{fmt(gt.total)} 원</div>
          <div className={styles.cellNote} />
        </div>

        {isOpen &&
          unitKeys.map((key) => {
            const unit = UNITS.find((u) => u.key === key);
            return unit ? renderUnit(unit, 2) : null;
          })}
      </React.Fragment>
    );
  };

  // ── 렌더 ──────────────────────────────────────────────────────────────────
  return (
    <div className={styles.wrap}>
      {/* 필터 */}
      <div className={styles.filterBar}>
        <label>기간</label>
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          className={styles.dateInput}
        />
        <span className={styles.dateDash}>~</span>
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          className={styles.dateInput}
        />
      </div>

      {/* 헤더 */}
      <div className={styles.headerRow}>
        <div className={styles.headerCell}>구분</div>
        <div className={styles.headerCell}>공급가</div>
        <div className={styles.headerCell}>부가세</div>
        <div className={styles.headerCell}>집행금액</div>
        <div className={styles.headerCell}>비고</div>
      </div>

      {/* 트리 */}
      {loading ? (
        <div className={styles.loading}>불러오는 중...</div>
      ) : (
        <div className={styles.tree}>
          {/* 경영팀 (단독) */}
          {renderUnit(UNITS.find((u) => u.key === "경영팀"), 1)}

          {/* 용역 (단독) */}
          {renderUnit(UNITS.find((u) => u.key === "용역"), 1)}

          {/* 환경사업 그룹 */}
          {renderGroup("환경사업", "환경", ["환경/소독", "환경/종합"])}

          {/* 카페사업 그룹 */}
          {renderGroup("카페사업", "카페", ["카페_가산", "카페_독산", "카페_금나래", "카페_그린"])}



          {/* 합계 */}
          <div className={`${styles.row} ${styles.totalRow}`}>
            <div className={styles.cell}>합 계</div>
            <div className={styles.cellRight}>{fmt(grandTotal.supply)} 원</div>
            <div className={styles.cellRight}>{fmt(grandTotal.vat)} 원</div>
            <div className={styles.cellRight}>{fmt(grandTotal.total)} 원</div>
            <div className={styles.cellNote} />
          </div>
        </div>
      )}
    </div>
  );
}